/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue';
import Router from 'vue-router';
import {
  userService,
  validatePaidSubscription
} from './appContainer';
import { setAppLocale } from './i18n';
import store from './store/store.js';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return {
      x: 0,
      y: 0
    };
  },
  routes: [{
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '/',
      component: () => import('./layouts/main/Main.vue'),
      props: true,
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '',
          name: 'home',
          component: () => import('./views/Home.vue'),
          props: true,
        },
        {
          path: 'change-password',
          name: 'change-password',
          component: () => import('./views/pages/ChangePassword.vue'),
          props: true,
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('./views/pages/Profile.vue'),
        },
        {
          path: 'table',
          name: 'aggregate-table',
          component: () => import('./views/agGridTables/AgGridTable.vue'),
          props: (route) => ({
            projectId: route.params.projectId
          }),
        },
        {
          path: 'capacities',
          name: 'capacities',
          component: () => import('./views/agGridTables/CapacitiesTable.vue'),
        },
        {
          path: 'timesheet',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'timesheet',
          component: () => import('./views/agGridTables/EmployeeTimesheet.vue'),
        },
        {
          path: 'settings',
          props: (route) => ({
            companyId: route.params.companyId
          }),

          name: 'settings',
          component: () => import('./views/Settings.vue'),
        },
        {
          path: 'employees',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'employees',
          component: () => import('./views/Employees.vue'),
        },
        {
          path: 'projects',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'projects',
          component: () => import('./views/Projects.vue'),
        },
        {
          path: 'work-reports',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'work-reports',
          component: () => import('./views/agGridTables/WorkReportsTable.vue'),
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('./layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/login',
          name: 'page-login',
          component: () => import('./views/pages/Login.vue'),
        },
        {
          path: '/logout',
          name: 'page-logout',
          component: () => import('./views/pages/Logout.vue'),
        },
        {
          path: '/forgotten-password',
          name: 'forgotten-password',
          component: () => import('./views/pages/ForgottenPassword.vue'),
        },
        {
          path: '/register',
          name: 'register',
          component: () => import('./views/pages/Register.vue'),
        },
        {
          path: '/reset-password',
          name: 'reset-password',
          component: () => import('./views/pages/ResetPassword.vue'),
          props: (route) => ({
            token: route.params.token
          }),
        },
        {
          path: '/checkout',
          name: 'checkout',
          component: () => import('./views/pages/Checkout.vue'),
          props: (route) => ({
            companyPublicId: route.query.companyId
          }),

        },
        {
          path: '/paid',
          name: 'payment-paid',
          component: () => import('./views/pages/PaymentStatus.vue'),
          props: {
            status: 'paid',
          },
        },
        {
          path: '/cancelled',
          name: 'payment-cancelled',
          component: () => import('./views/pages/PaymentStatus.vue'),
          props: {
            status: 'cancelled',
          },
        },
        {
          path: '/pending',
          name: 'payment-pending',
          component: () => import('./views/pages/PaymentStatus.vue'),
          props: {
            status: 'pending',
          },
        },
        {
          path: '/terms',
          name: 'terms',
          component: () => import('./views/pages/Terms.vue'),
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('./views/pages/Error404.vue'),
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404',
    },
  ],
});

const nonRedirectedPaths = [
  'page-login',
  'page-logout',
  'register',
  'forgotten-password',
  'reset-password',
  'terms',
  'payment-paid',
  'payment-cancelled',
  'payment-pending',
];
const companyTabsPermissions = [{
    path: '/table',
    permission: 'cashflow_view',
  },
  {
    path: '/capacities',
    permission: 'capacities_view',
  },
  {
    path: '/employees',
    permission: 'people_view',
  },
  {
    path: '/projects',
    permission: 'projects_view',
  },
  {
    path: '/timesheet',
    permission: 'timesheets_view',
  },
  {
    path: '/settings',
  },
];

router.beforeEach(async (to, _from, next) => {
  try {
    const {
      userInfo
    } = await userService.userIsLoggedIn();
    setAppLocale(userInfo?.locale);
    store.dispatch('updateUserInfo', userInfo);
    await store.dispatch('updateIsLoggedIn', true);
    const companyTabObject = companyTabsPermissions.find((t) => t.path === to.path);
    if (!validatePaidSubscription.checkSubscriptionAccess(to, store)) {
      // const companyId = validatePaidSubscription.resolveCompanyId(to, store);
      // if (companyId) {
      //   next({ name: 'checkout', query: { companyId } });
      //   return;
      // }
      next('/');
      return;
    }
    // Check permission for the tab
    if (
      Object.keys(store.state.user.companies).some(store.getters.isAdmin) ||
      companyTabObject === undefined ||
      (companyTabObject &&
        store.getters.hasPermission(to.query.projectId, companyTabObject.permission))
    ) {
      next();
    } else {
      next('/');
    }
  } catch (e) {
    const status = e.response?.status;
    if (status === 401) {
      await store.dispatch('updateIsLoggedIn', false);
      if (!nonRedirectedPaths.includes(to.name)) {
        next('/login');
        return;
      }
    }
    next();
  }
});
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

export default router;
